import { booleanAttribute, ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';

import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    template: `
        <div class="vz-confirm-root fit layout center-center">
            @if (showBackdrop() && !hiding()) {
                <div class="vz-confirm-backdrop fit" (click)="onCancelClick($event)" animate.enter="vza-show-op" animate.leave="vza-hide-op"></div>
            }
            @if (!hiding()) {
                <div class="vz-confirm" [style.max-width]="maxWidth()" animate.enter="vza-show-scale" animate.leave="vza-hide-scale">
                    <div class="vz-confirm-wrap layout start">
                        <div class="vz-confirm-icon">
                            <i class="vzi-question" [class.txt-warn]="!okDanger()" [class.txt-err]="okDanger()"></i>
                        </div>
                        <div>
                            <div class="vz-confirm-title">{{ title() }}</div>
                            @if (content(); as text) {
                                <div class="vz-confirm-content">{{ text }}</div>
                            }
                        </div>
                    </div>
                    <div class="vz-confirm-buttons layout">
                        <button nz-button (click)="onCancelClick($event)">
                            {{ cancelText() }}
                        </button>
                        <div class="flex"></div>
                        <button nz-button nzType="primary" [nzDanger]="okDanger()" (click)="onOkClick($event)">
                            {{ okText() }}
                        </button>
                    </div>
                </div>
            }
        </div>
    `,
    styles: `
        @import 'theme';
        .vz-confirm-root {
            .vz-confirm-backdrop {
                background-color: @bgColorInv;
                opacity: .5;
            }
            .vz-confirm {
                min-width: 250px;
                overflow: hidden;
                padding: 16px;
                background-color: @bgColor;
                box-shadow: 0 0 10px 3px rgba(@bgColorInv, .5);
                z-index: 2;
                .vz-confirm-wrap {
                    .vz-confirm-icon {
                        padding: 4px;
                        font-size: 24px;
                    }
                    .vz-confirm-title {
                        padding: 4px 8px;
                        min-width: 0;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        font-weight: bold;
                        color: @primaryColor;
                    }
                    .vz-confirm-content {
                        padding: 4px 8px;
                        max-height: min(500px, 50%);
                        overflow: auto;
                    }
                }
                .vz-confirm-buttons {
                    padding: 4px 8px;
                    margin-top: 8px;
                }
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NzButtonModule],
})
export class VzConfirmComponent {

    readonly title          = input<string>();
    readonly content        = input<string>();
    readonly okText         = input<string>('OK');
    readonly cancelText     = input<string>('Отмена');
    readonly okDanger       = input<boolean, unknown>(false, { transform: booleanAttribute });
    readonly maxWidth       = input<string>('400px');
    readonly backdropCancel = input<boolean, unknown>(true, { transform: booleanAttribute });
    readonly showBackdrop   = input<boolean, unknown>(true, { transform: booleanAttribute });

    readonly okClick        = output<MouseEvent | void>();
    readonly cancelClick    = output<MouseEvent | void>();

    readonly hiding         = signal<boolean>(false);

    private readonly _destroy = inject(DestroyRef);

    keydownFn = (e: KeyboardEvent) => this.keyDown(e);

    constructor () {
        document.addEventListener('keydown', this.keydownFn, { capture: true });
        this._destroy.onDestroy(() => {
            document.removeEventListener('keydown', this.keydownFn, { capture: true });
        });
    }

    onOkClick(e?: MouseEvent): void {
        this.hiding.set(true);
        document.removeEventListener('keydown', this.keydownFn)
        setTimeout(() => this.okClick.emit(e), 150);
    }

    onCancelClick(e?: MouseEvent): void {
        this.hiding.set(true);
        document.removeEventListener('keydown', this.keydownFn)
        setTimeout(() => this.cancelClick.emit(e), 150);
    }

    keyDown(e: KeyboardEvent): void {
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (e && !e.ctrlKey && !e.shiftKey && !e.altKey && !e.metaKey && (e.key == 'Escape' || e.key == 'Enter')) {
            if (e.key == 'Escape') {
                this.onCancelClick();
            }
            else if (e.key == 'Enter') {
                this.onOkClick();
            }
        }
    }

}
